import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellTitle, CellCommon } from '@root/components/Cell';
import { useNavigate } from 'react-router-dom';
import useSubscriptionList from '@root/hooks/subscription/useList';
import Empty from '@root/components/Empty';
import SectionLoading from '@root/components/SectionLoading';
import { IPackageSubscription } from '@root/types/package';
import { EPayMode, UNLIMITED_PACKAGE_NUMBER } from '@root/constant';
import { isInIosApp } from '@root/shared';
import Icon from '@root/components/Icon';
import useApplePayBtnAction from '@root/hooks/applePay/useApplePayBtnAction';
import './index.less';

const SubscribedList = () => {
  const { t } = useTranslation();
  const { list, loading } = useSubscriptionList();
  const navigate = useNavigate();
  const onApplePay = useApplePayBtnAction();

  const onSubscribe = useCallback(() => {
    // if (!isInApp) {
    //   navigate('/packages');
    //   return;
    // }

    if (isInIosApp) {
      onApplePay();
      return;
    }

    navigate('/defaultPackage');
  }, [navigate, onApplePay]);

  const onDetail = useCallback(
    (id: string) => () => {
      navigate(`/subscription/${id}`);
    },
    [navigate],
  );

  const onGiftCard = useCallback(() => {
    navigate('/giftCard');
  }, [navigate]);

  const getContend = useCallback(
    (item: IPackageSubscription) => {
      const { packageType, subscribeStatus, expiredDays, subscribeCycle } =
        item;

      if (packageType < 1) {
        return '';
      }

      if (packageType === 1 && subscribeStatus === 1) {
        if (subscribeCycle === 'year') {
          return t('year_renewal');
        }
        if (subscribeCycle === 'month') {
          return t('month_renewal');
        }
        if (subscribeCycle === 'day') {
          return t('day_renewal');
        }
      }
      return `${expiredDays} ${t('day_left')}`;
    },
    [t],
  );

  const payModeText: any = useMemo(() => {
    return {
      [EPayMode.PayPal]: t('payPal'),
      [EPayMode.ApplePay]: t('apple'),
      [EPayMode.CreditCard]: t('credit_card'),
    };
  }, [t]);

  return (
    <>
      <CellTitle paddingTop={12}>
        <div className="subscribed-list-label">
          <span className="left">{t('subscribed_service')}</span>
          {!isInIosApp && (
            <span className="right cursor-pointer" onClick={onGiftCard}>
              {t('redeem_gift_card')}
            </span>
          )}
        </div>
      </CellTitle>
      {loading < 1 && <SectionLoading />}
      {loading > 0 && list.length > 0 && (
        <div className="subscribed-list">
          {list.map((item: any) => {
            const _support =
              item.buyNum === UNLIMITED_PACKAGE_NUMBER
                ? `${t('unlimited_devices')}`
                : `${item.supportedDevNum} ${
                    item.supportedDevNum > 1
                      ? t('plan_list_devices')
                      : t('plan_list_device')
                  }`;
            return (
              <CellCommon
                key={item.packageId}
                title={
                  <h2>
                    {item.packageName}
                    {!!payModeText[item.payMode] && (
                      <span>{`(${payModeText[item.payMode]})`}</span>
                    )}
                  </h2>
                }
                description={
                  <>
                    <p>{`${item.devNum}/${_support}`}</p>
                    <p>{getContend(item)}</p>
                  </>
                }
                arrow
                onClick={onDetail(item.packageId)}
              />
            );
          })}
        </div>
      )}

      {loading > 0 && list.length === 0 && (
        <Empty>{t('subscription_empty')}</Empty>
      )}

      {loading > 0 && (
        <div className="add-new-subscribe" onClick={onSubscribe}>
          <Icon type="list_add" fontSize={24} />
          {t('add_new_subscription')}
        </div>
      )}
    </>
  );
};

export default SubscribedList;
